import { skipToken, useMutation, useQuery } from '@tanstack/react-query'

import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import type { ITodolistFlow, ITodolistTaskGroup, TODOLIST_TASK_STATUS } from '~/types/todolist'

import { createTodolist, getTodolist, getTodolists, updateTodolist, updateTodolistTaskStatus } from '~/api/todolists'

////////// KEYS //////////

export const keys = {
  all: ['todolists'] as const,
  details: (todolistId: string) => [...keys.all, 'details', todolistId] as const,
  list: (params: { appKey?: string; deviceType?: string }) => [...keys.all, 'list', params] as const,
}

////////// QUERIES //////////

export const useTodolistsListQuery = ({
  appKey,
  deviceType,
}: {
  appKey?: string
  deviceType?: string
}): UseQueryResult<ITodolistFlow[], unknown> => {
  return useQuery({
    queryFn: appKey && deviceType ? () => getTodolists({ appKey, deviceType }) : skipToken,
    queryKey: keys.list({ appKey, deviceType }),
  })
}

export const useTodolistsDetailsQuery = (todolistId: string): UseQueryResult<ITodolistFlow, unknown> => {
  return useQuery({
    queryFn: () => getTodolist(todolistId),
    queryKey: keys.details(todolistId),
  })
}

//////// MUTATIONS //////////

export const useCreateTodolistMutation = (): UseMutationResult<
  ITodolistFlow,
  unknown,
  {
    app_key: string
    device_type: string
    groups: ITodolistTaskGroup[]
    template_id: string
    title: string
  }
> => {
  return useMutation({ mutationFn: createTodolist })
}

export const useUpdateTodolistMutation = (): UseMutationResult<
  ITodolistFlow,
  unknown,
  { groups: ITodolistTaskGroup[]; title: string; todolistId: string }
> => {
  return useMutation({ mutationFn: updateTodolist })
}

export const useUpdateTodolistTaskStatusMutation = (): UseMutationResult<
  void,
  unknown,
  {
    status: TODOLIST_TASK_STATUS
    taskId: string
    todolistId: string
  },
  unknown
> => {
  return useMutation({ mutationFn: updateTodolistTaskStatus })
}
